/**
 * QuickEditCustomerDialog
 * ----------------------------------------------------------------------
 * Small in-modal editor for customer name / phone / email / address so
 * SM can clean up a walk-in's details from inside the Job Card modal
 * without bouncing to the Customers page. Mirrors the larger Edit Customer
 * dialog used in InvoiceEdit but trimmed down to the fields that matter
 * at drop-off time.
 *
 * Calls PUT /api/customers/{id} then fires onSaved() so the parent can
 * refresh its denormalized snapshot.
 */
import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  display_name: '', first_name: '', last_name: '',
  email: '', mobile: '', phone: '',
  address_line1: '', address_line2: '',
  suburb: '', state: '', postcode: '',
  company: '', abn: '',
};

export default function QuickEditCustomerDialog({ open, onOpenChange, customerId, jobCardId, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !customerId) return;
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${BACKEND_URL}/api/customers/${customerId}`, {
          credentials: 'include',
        });
        if (!r.ok) throw new Error('Failed to load');
        const c = await r.json();
        if (!active) return;
        setForm({
          display_name: c.display_name || '',
          first_name: c.first_name || '',
          last_name: c.last_name || '',
          email: c.email || '',
          mobile: c.mobile || '',
          phone: c.phone || '',
          address_line1: c.address_line1 || c.address || '',
          address_line2: c.address_line2 || '',
          suburb: c.suburb || '',
          state: c.state || '',
          postcode: c.postcode || '',
          company: c.company || '',
          abn: c.abn || '',
        });
      } catch (_e) {
        toast.error('Could not load customer');
        onOpenChange(false);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [open, customerId, onOpenChange]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    const displayName = form.display_name?.trim()
      || `${form.first_name || ''} ${form.last_name || ''}`.trim();
    if (!displayName) {
      toast.error('Customer name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, display_name: displayName };
      // Convert empty strings to null so backend's "skip-None" rule keeps
      // untouched fields intact rather than wiping them.
      Object.keys(payload).forEach((k) => {
        if (payload[k] === '' || payload[k] === undefined) payload[k] = null;
      });
      // When this dialog was opened from a JC modal, pass force_sync_jc_id
      // so the JC snapshot + linked invoice/quote get force-synced even
      // if their `customer_name` had drifted to a wrong value. Without
      // this, the drift guard correctly skips wrong-name docs — but the
      // SM is explicitly trying to fix THIS JC's customer.
      const url = jobCardId
        ? `${BACKEND_URL}/api/customers/${customerId}?force_sync_jc_id=${encodeURIComponent(jobCardId)}`
        : `${BACKEND_URL}/api/customers/${customerId}`;
      const r = await fetch(url, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || 'Save failed');
        return;
      }
      toast.success('Customer details saved');
      onSaved?.();
      onOpenChange(false);
    } catch (_e) {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="quick-edit-customer-dialog">
        <DialogHeader>
          <DialogTitle>Edit customer details</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-slate-500">Loading…</div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label htmlFor="qec-display">Display name</Label>
              <Input id="qec-display" data-testid="qec-display-name"
                value={form.display_name} onChange={set('display_name')}
                placeholder="John Smith" />
            </div>
            <div>
              <Label htmlFor="qec-first">First name</Label>
              <Input id="qec-first" data-testid="qec-first-name"
                value={form.first_name} onChange={set('first_name')} />
            </div>
            <div>
              <Label htmlFor="qec-last">Last name</Label>
              <Input id="qec-last" data-testid="qec-last-name"
                value={form.last_name} onChange={set('last_name')} />
            </div>
            <div>
              <Label htmlFor="qec-mobile">Mobile (saved as 0415 837 157)</Label>
              <Input id="qec-mobile" data-testid="qec-mobile"
                value={form.mobile} onChange={set('mobile')}
                placeholder="0415 837 157" />
            </div>
            <div>
              <Label htmlFor="qec-phone">Phone</Label>
              <Input id="qec-phone" data-testid="qec-phone"
                value={form.phone} onChange={set('phone')}
                placeholder="03 9876 5432" />
            </div>
            <div className="col-span-2">
              <Label htmlFor="qec-email">Email</Label>
              <Input id="qec-email" data-testid="qec-email" type="email"
                value={form.email} onChange={set('email')} />
            </div>
            <div className="col-span-2">
              <Label htmlFor="qec-addr1">Address line 1</Label>
              <Input id="qec-addr1" data-testid="qec-addr1"
                value={form.address_line1} onChange={set('address_line1')} />
            </div>
            <div className="col-span-2">
              <Label htmlFor="qec-addr2">Address line 2</Label>
              <Input id="qec-addr2" data-testid="qec-addr2"
                value={form.address_line2} onChange={set('address_line2')} />
            </div>
            <div>
              <Label htmlFor="qec-suburb">Suburb</Label>
              <Input id="qec-suburb" data-testid="qec-suburb"
                value={form.suburb} onChange={set('suburb')} />
            </div>
            <div>
              <Label htmlFor="qec-state">State</Label>
              <Input id="qec-state" data-testid="qec-state"
                value={form.state} onChange={set('state')} placeholder="VIC" />
            </div>
            <div>
              <Label htmlFor="qec-postcode">Postcode</Label>
              <Input id="qec-postcode" data-testid="qec-postcode"
                value={form.postcode} onChange={set('postcode')} placeholder="3000" />
            </div>
            <div>
              <Label htmlFor="qec-company">Company</Label>
              <Input id="qec-company" data-testid="qec-company"
                value={form.company} onChange={set('company')}
                placeholder="ACME Pty Ltd (optional)" />
            </div>
            <div>
              <Label htmlFor="qec-abn">ABN</Label>
              <Input id="qec-abn" data-testid="qec-abn"
                value={form.abn} onChange={set('abn')}
                placeholder="12 345 678 901 (optional)" />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="qec-cancel">
            Cancel
          </Button>
          <Button onClick={save} disabled={saving || loading} data-testid="qec-save">
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
